import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { RowDensity } from '../../../enum/RowDensity';
import { getWindowWidth, isMobileScreen } from '../../../util/mobile';
import { SidebarMenu } from '../../../enum/SidebarMenu';
import { ApiConsumer } from '../../../service/api-consumer';
import { AgmsResponseCode } from '../../../enum/AgmsResponseCode';
import { getConfigFromLocalStorage, setConfigToLocalStorage } from '../../../util/local-storage';
import { ApplicationConfig } from '../../../interface/response/ApplicationConfig';
import { AppDispatch } from '../../store';
import { updateApplicationConfig } from '../../../util/update-config';
import { DEFAULT_TIMEZONE } from '../../../constant/constants';
import { ThemeEnum } from '../../../enum/Theme';
import { applyTheme } from '../../../theme/applyTheme';

/*
 * This slice stores all user-specific persistent configuration settings used across the app.
 * These settings include:
 *
 *   • Row Density
 *   • Time Zone
 *   • Theme (Dark / Light)
 *   • Thumbnail View Toggle
 *   • Sidebar State (Minimized / Expanded)
 *   • Active Sidebar Menu
 *   • Mobile/Desktop Mode Flag
 *
 * The goal is to have ONE single source of truth for all user UI preferences.
 * This ensures consistent behavior across:
 *   – Page reloads
 *   – Different tabs
 *   – Different browsers
 *   – Different devices
 *
 *
 * DATA FLOW OVERVIEW
 * -------------------
 *   Backend (User Config)
 *          │
 *          │  loadConfigFromBackend()
 *          │  getConfig()
 *          ▼
 *   Redux configSlice
 *       │
 *       │  saveConfigToBackend()
 *       │  updateConfig()
 *       ▼
 *   LocalStorage
 *
 * Backend values override localStorage values.
 * LocalStorage acts as a fallback when backend is unavailable.
 *
 * ASYNC LOGIC
 * -----------
 *
 * getConfig
 *    Performs a direct API call to fetch user configuration.
 *    Returns the raw backend response object (ResponseCode, Config, Error, ...).
 *
 * updateConfig
 *    Sends the full ApplicationConfig object to the backend.
 *    Returns the raw backend response.
 *
 * loadConfigFromBackend
 *      - Fetches user config from backend
 *      - Falls back to localStorage if backend fails
 *      - Applies theme immediately (side effect)
 *
 * saveConfigToBackend
 *      - Updates backend with only modified fields
 *      - Updates Redux state
 *      - Saves the final merged config to localStorage
 *
 *
 * SPECIAL BEHAVIOR — THEME
 * -------------------------
 * Unlike other settings, "theme" has a mandatory side effect:
 *      applyTheme(theme)
 *
 * Because it modifies HTML/CSS variables, theme updates are only applied when a
 * valid theme value is received either from:
 *      • user interaction (setTheme)
 *      • backend (loadConfigFromBackend)
 *
 */

interface Config {
  isThumbnailView: boolean;
  graphTimeMinutesStep: number;
  selectedRowDensity: RowDensity;
  isMobile: boolean;
  isSidebarMinimized: boolean;
  activeSidebarElement: SidebarMenu;
  rowDensity?: RowDensity;
  timeZone: string;
  theme: ThemeEnum;
}

const initialState: Config = {
  isThumbnailView: false,
  graphTimeMinutesStep: 15,
  selectedRowDensity: getConfigFromLocalStorage()?.rowDensity || RowDensity.NORMAL,
  isMobile: isMobileScreen(getWindowWidth()),
  isSidebarMinimized: false,
  activeSidebarElement: SidebarMenu.NONE,
  rowDensity: getConfigFromLocalStorage()?.rowDensity || RowDensity.NORMAL,
  timeZone: getConfigFromLocalStorage()?.timeZone || DEFAULT_TIMEZONE,
  theme: getConfigFromLocalStorage()?.theme || ThemeEnum.LIGHT,
};

export const getConfig = createAsyncThunk('config/getConfig', async () => {
  const response = await ApiConsumer.getConfig();

  return response;
});

export const updateConfig = createAsyncThunk('config/updateConfig', async (config: ApplicationConfig) => {
  const response = await ApiConsumer.updateConfig(config);

  return response;
});

export const saveConfigToBackend = createAsyncThunk<
  Partial<ApplicationConfig>,
  Partial<ApplicationConfig>,
  { dispatch: AppDispatch }
>('config/saveConfigToBackend', async (newConfig, { dispatch }) => {
  await updateApplicationConfig(newConfig, dispatch);
  return newConfig;
});

export const loadConfigFromBackend = createAsyncThunk('config/loadConfigFromBackend', async () => {
  try {
    const response = await ApiConsumer.getConfig();

    if (response.ResponseCode === AgmsResponseCode.OK) {
      return {
        rowDensity: response.Config?.rowDensity ?? getConfigFromLocalStorage()?.rowDensity,
        timeZone: response.Config?.timeZone ?? getConfigFromLocalStorage()?.timeZone,
        theme: response.Config?.theme ?? getConfigFromLocalStorage()?.theme,
      };
    }
  } catch (err) {
    return {
      rowDensity: getConfigFromLocalStorage()?.rowDensity,
      timeZone: getConfigFromLocalStorage()?.timeZone,
      theme: getConfigFromLocalStorage()?.theme,
    };
  }
});

const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    setIsThumbnailView: (state) => {
      state.isThumbnailView = !state.isThumbnailView;
    },
    setSelectedRowDensity: (state, { payload }) => {
      state.selectedRowDensity = payload;
    },
    setSelectedTimeZone: (state, { payload }) => {
      state.timeZone = payload;
    },
    setTheme(state, { payload }) {
      state.theme = payload;
      applyTheme(payload);
    },

    setIsMobile(state, { payload }) {
      state.isMobile = payload;
    },
    setIsSidebarMinimized(state) {
      state.isSidebarMinimized = !state.isSidebarMinimized;
    },
    setActiveSidebarElement(state, { payload }) {
      state.activeSidebarElement = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getConfig.fulfilled, (state, { payload }) => {
      if (payload.ResponseCode === AgmsResponseCode.OK) {
        setConfigToLocalStorage(payload.Config);
      }
    });
    builder.addCase(loadConfigFromBackend.fulfilled, (state, action) => {
      const payload = action.payload ?? getConfigFromLocalStorage();

      state.timeZone = payload?.timeZone ?? state.timeZone;
      state.rowDensity = payload?.rowDensity ?? state.rowDensity;
      state.selectedRowDensity = payload?.rowDensity ?? state.selectedRowDensity;

      if (payload?.theme) {
        state.theme = payload.theme;
        applyTheme(payload.theme);
      }
    });

    builder.addCase(saveConfigToBackend.fulfilled, (state, action) => {
      const payload = action.payload;

      state.rowDensity = payload.rowDensity ?? state.rowDensity;
      state.selectedRowDensity = payload.rowDensity ?? state.selectedRowDensity;
      state.timeZone = payload.timeZone ?? state.timeZone;

      if (payload.theme) {
        state.theme = payload.theme;
        applyTheme(payload.theme);
      }
      setConfigToLocalStorage({
        rowDensity: state.rowDensity,
        timeZone: state.timeZone,
        theme: state.theme,
      });
    });

    builder.addCase(updateConfig.fulfilled, (state, { payload }) => {
      if (payload.ResponseCode === AgmsResponseCode.OK) {
        setConfigToLocalStorage(payload.Config);
      }
    });
  },
});

export const {
  setIsThumbnailView,
  setSelectedRowDensity,
  setSelectedTimeZone,
  setTheme,
  setIsMobile,
  setIsSidebarMinimized,
  setActiveSidebarElement,
} = configSlice.actions;

export default configSlice.reducer;
